import { Card } from "@/components/common/Ornament";

export const PHASES = {
  design: { color: "#0F2E23", label: "Design" },
  permit: { color: "#C59B27", label: "Permit / Bidding / Contract" },
  construction: { color: "#B5683B", label: "Construction" },
};

const MS = 86400000;
const short = (t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

function months(min, max) {
  const out = [];
  const d = new Date(min);
  while (d.getTime() < max) {
    out.push(d.getTime());
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return out;
}

function Row({ it, pos, i }) {
  const s = Date.parse(it.start);
  const e = Date.parse(it.end || it.start);
  const milestone = e - s <= 10 * MS;
  const color = (PHASES[it.phase] || PHASES.design).color;
  return (
    <div className="flex items-center border-b border-dashed border-[#E2DCD2] last:border-0 py-2" data-testid={`timeline-row-${i}`}>
      <div className="w-56 sm:w-72 shrink-0 pr-3">
        <p className="text-xs sm:text-sm text-[#0F2E23] font-medium leading-snug">{it.label}</p>
        <p className="text-[0.65rem] font-mono-num text-slate-500">{milestone ? short(e) : `${short(s)} – ${short(e)}`}</p>
      </div>
      <div className="relative flex-1 h-6">
        {milestone ? (
          <span className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border-2 border-white shadow" style={{ left: `${pos(e)}%`, background: color }} />
        ) : (
          <span className="absolute top-1 h-4 rounded-sm shadow-sm" style={{ left: `${pos(s)}%`, width: `${Math.max(pos(e) - pos(s), 0.8)}%`, background: color }} />
        )}
      </div>
    </div>
  );
}

export default function ProjectTimeline({ items = [] }) {
  const valid = items.filter((i) => i.start && !Number.isNaN(Date.parse(i.start))).sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  if (!valid.length) return null;
  const first = new Date(Math.min(...valid.map((i) => Date.parse(i.start))));
  const last = new Date(Math.max(...valid.map((i) => Date.parse(i.end || i.start))));
  const min = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
  const max = Date.UTC(last.getUTCFullYear(), last.getUTCMonth() + 1, 1);
  const pos = (t) => ((t - min) / (max - min)) * 100;
  const ticks = months(min, max);
  const today = Date.now();

  return (
    <Card eyebrow="Project Schedule" title="Design, Permit & Construction Timeline" testId="overview-timeline"
      right={<div className="hidden md:flex gap-4 text-xs">{Object.values(PHASES).map((p) => <span key={p.label} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{p.label}</span>)}</div>}>
      <div className="flex md:hidden flex-wrap gap-3 text-xs mb-3">{Object.values(PHASES).map((p) => <span key={p.label} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{p.label}</span>)}</div>
      <div className="overflow-x-auto -mx-2 px-2">
        <div className="min-w-[860px]">
          <div className="flex">
            <div className="w-56 sm:w-72 shrink-0" />
            <div className="relative flex-1 h-6 border-b border-[#0F2E23]">
              {ticks.map((t, k) => (
                <span key={t} className="absolute top-0 text-[0.6rem] font-mono-num text-[#3E5C4E] -translate-x-0" style={{ left: `${pos(t)}%` }}>
                  {k % 2 === 0 ? new Date(t).toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" }) : ""}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            {valid.map((it, i) => <Row key={i} it={it} pos={pos} i={i} />)}
            {today > min && today < max && (
              <div className="absolute inset-y-0 flex" style={{ left: 0, right: 0 }} aria-hidden="true">
                <div className="w-56 sm:w-72 shrink-0" />
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 border-l-2 border-dashed border-red-500/60" style={{ left: `${pos(today)}%` }} data-testid="timeline-today-marker">
                    <span className="absolute -top-0.5 left-1 text-[0.6rem] font-semibold text-red-600 bg-white/90 px-1 rounded">Today</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-4">Source: AQ Studios project schedule, revised 07/06/2026. Dates are approximate and subject to change.</p>
    </Card>
  );
}
